import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { EVENT_LEADERBOARD_QUERY_KEY } from '@/components/events/live/useEventLeaderboard';

/**
 * Photo-review status for a submission. Mirrors the Prisma `PhotoStatus`
 * enum on `apps/hunts/api/prisma/models/submission.prisma`. Single source of
 * truth reused by the gallery pills, cards, and review drawer.
 */
export const PHOTO_STATUS = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export type SubmissionPhotoStatus = (typeof PHOTO_STATUS)[keyof typeof PHOTO_STATUS];

/**
 * Status-filter values for the gallery pills. `all` is a UI-only sentinel
 * that clears the `photoStatus` query param.
 */
export const STATUS_FILTER = {
  all: 'all',
  pending: PHOTO_STATUS.pending,
  approved: PHOTO_STATUS.approved,
  rejected: PHOTO_STATUS.rejected,
} as const;

export type StatusFilter = (typeof STATUS_FILTER)[keyof typeof STATUS_FILTER];

/** Review actions accepted by the review endpoint. */
export const REVIEW_ACTION = {
  approve: 'approve',
  reject: 'reject',
} as const;

export type ReviewAction = (typeof REVIEW_ACTION)[keyof typeof REVIEW_ACTION];

/** Default number of submissions requested per page. */
export const DEFAULT_PAGE_SIZE = 24;

/** React Query key roots — exported so callers can invalidate/prefetch. */
export const SUBMISSIONS_QUERY_KEY = 'EventSubmissions';
export const SUBMISSIONS_SUMMARY_QUERY_KEY = 'EventSubmissionsSummary';

/** Minimal team reference embedded on each submission row. */
export interface SubmissionTeamRef {
  id: string;
  name: string;
}

/** Minimal challenge reference embedded on each submission row. */
export interface SubmissionChallengeRef {
  id: string;
  name: string;
  type: string;
  points: number;
  answerType: string;
  /** Review context — what the players were actually asked to do. */
  clue?: string | null;
  description?: string | null;
  imageUrl?: string | null;
}

/** A single submission row returned by the list endpoint. */
export interface Submission {
  id: string;
  teamId: string;
  challengeId: string;
  userId: string | null;
  displayName: string;
  answer: string;
  isCorrect: boolean;
  pointsAwarded: number;
  bonusAwarded: number;
  completionPosition: number | null;
  photoProofUrl: string | null;
  photoStatus: SubmissionPhotoStatus | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  submittedAt: string;
  aiValidated: boolean;
  aiConfidence: number | null;
  aiReasoning: string | null;
  team: SubmissionTeamRef;
  challenge: SubmissionChallengeRef;
}

/** Standard pagination envelope shared across list endpoints. */
export interface Pagination {
  total: number;
  page: number;
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
}

/** Response shape of `GET /events/:eventId/submissions`. */
export interface SubmissionsResponse {
  success: boolean;
  data: Submission[];
  pagination: Pagination;
}

/** Aggregate counts by review status. Keys match the PhotoStatus enum. */
export interface SubmissionsSummary {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
}

/** Response shape of `GET /events/:eventId/submissions/summary`. */
export interface SubmissionsSummaryResponse {
  success: boolean;
  data: SubmissionsSummary;
}

/** Query filters accepted by {@link useEventSubmissions}. */
export interface SubmissionsFilters {
  photoStatus?: SubmissionPhotoStatus;
  challengeId?: string;
  teamId?: string;
  hasPhoto?: boolean;
  isCorrect?: boolean;
  page?: number;
  limit?: number;
}

/** Input for {@link useReviewSubmission}'s mutate call. */
export interface ReviewSubmissionInput {
  submissionId: string;
  action: ReviewAction;
  points?: number;
  /** Required by the API when action = reject; the team sees it verbatim. */
  reason?: string;
}

/**
 * Saved rejection reasons — one tap for the common verdicts. Free text is
 * always available for anything not covered.
 */
export const REJECTION_REASONS = [
  "Photo doesn't match the challenge",
  'Too blurry to verify',
  'Wrong location',
  'Not enough of the team in the shot',
  'Inappropriate content',
] as const;

/** A lightweight `{ id, name }` option for the filter dropdowns. */
export interface FilterOption {
  id: string;
  name: string;
}

/**
 * Builds the request query params for the submissions list, omitting
 * `undefined` filters so the URL stays clean and cache keys are stable.
 *
 * @param filters - Active submission filters.
 * @returns A flat params record safe to pass to `apiClient.get`.
 */
function toSubmissionParams(
  filters: SubmissionsFilters,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {
    page: filters.page ?? 1,
    limit: filters.limit ?? DEFAULT_PAGE_SIZE,
  };
  if (filters.photoStatus) params.photoStatus = filters.photoStatus;
  if (filters.challengeId) params.challengeId = filters.challengeId;
  if (filters.teamId) params.teamId = filters.teamId;
  if (filters.hasPhoto !== undefined) params.hasPhoto = filters.hasPhoto;
  if (filters.isCorrect !== undefined) params.isCorrect = filters.isCorrect;
  return params;
}

/**
 * Fetches the paginated, filtered submissions for an event.
 *
 * @param eventId - The event whose submissions to load.
 * @param filters - Status / challenge / team / pagination filters.
 * @returns A TanStack Query result of {@link SubmissionsResponse}.
 */
export function useEventSubmissions(eventId: string | undefined, filters: SubmissionsFilters) {
  const apiClient = useApiClient();
  return useQuery<SubmissionsResponse>({
    queryKey: [SUBMISSIONS_QUERY_KEY, eventId, filters],
    queryFn: () =>
      apiClient.get(`/events/${eventId}/submissions`, {
        params: toSubmissionParams(filters),
      }),
    enabled: Boolean(eventId),
    placeholderData: (previous) => previous,
  });
}

/**
 * Fetches the status-count summary for an event's submissions, powering the
 * live counts on the gallery filter pills.
 *
 * @param eventId - The event whose summary to load.
 * @returns A TanStack Query result of {@link SubmissionsSummaryResponse}.
 */
export function useSubmissionsSummary(eventId: string | undefined) {
  const apiClient = useApiClient();
  return useQuery<SubmissionsSummaryResponse>({
    queryKey: [SUBMISSIONS_SUMMARY_QUERY_KEY, eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/submissions/summary`),
    enabled: Boolean(eventId),
  });
}

/**
 * Mutation that approves or rejects a submission. On success it invalidates
 * the submissions list, the status summary, and the event leaderboard (points
 * change on approval), so every dependent surface refetches.
 *
 * @param eventId - The event the submission belongs to.
 * @returns A TanStack Mutation accepting {@link ReviewSubmissionInput}.
 */
export function useReviewSubmission(eventId: string | undefined) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ submissionId, action, points, reason }: ReviewSubmissionInput) =>
      apiClient.patch(`/events/${eventId}/submissions/${submissionId}/review`, {
        action,
        ...(points !== undefined ? { points } : {}),
        ...(reason !== undefined ? { reason } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SUBMISSIONS_QUERY_KEY, eventId] });
      queryClient.invalidateQueries({ queryKey: [SUBMISSIONS_SUMMARY_QUERY_KEY, eventId] });
      queryClient.invalidateQueries({ queryKey: [EVENT_LEADERBOARD_QUERY_KEY, eventId] });
    },
  });
}

/**
 * Loads the event's teams as `{ id, name }` options for the team filter.
 *
 * @param eventId - The event whose teams to load.
 * @returns A TanStack Query result of team filter options.
 */
export function useEventTeamOptions(eventId: string | undefined) {
  const apiClient = useApiClient();
  return useQuery<{ success: boolean; data: FilterOption[] }>({
    queryKey: ['EventTeams', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams`),
    enabled: Boolean(eventId),
    staleTime: 30_000,
  });
}

/**
 * Loads the event's challenges as `{ id, name }` options for the challenge
 * filter. Skipped when the gallery is already scoped to a fixed challenge.
 *
 * @param eventId - The event whose challenges to load.
 * @param enabled - Whether to run the query (false hides the challenge filter).
 * @returns A TanStack Query result of challenge filter options.
 */
export function useEventChallengeOptions(eventId: string | undefined, enabled = true) {
  const apiClient = useApiClient();
  return useQuery<{ success: boolean; data: FilterOption[] }>({
    queryKey: ['EventChallenges', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/challenges`),
    enabled: Boolean(eventId) && enabled,
    staleTime: 30_000,
  });
}
